import { translations } from '../i18n/strings.js';
import { getCookie } from './cookies.js';

/**
 * i18n utility for translations
 * Uses Proxy-based state management
 */

const DEFAULT_LANG = 'es';

// Local references (will be set during init)
let proxyState = null;
let availableLanguages = ['es', 'en'];

/**
 * Get current language from proxy state
 */
export function getCurrentLanguage() {
  if (proxyState) {
    return proxyState.lang || DEFAULT_LANG;
  }
  return DEFAULT_LANG;
}

/**
 * Get available languages
 */
export function getAvailableLanguages() {
  return availableLanguages;
}

/**
 * Set current language via proxy
 * @param {string} lang - Language code
 */
export function setLanguage(lang) {
  if (!translations[lang]) {
    console.warn(`[i18n] Language ${lang} not available, falling back to ${DEFAULT_LANG}`);
    lang = DEFAULT_LANG;
  }
  
  if (proxyState) {
    proxyState.lang = lang;
  }
}

/**
 * Translate a key using dot notation (e.g., 'app.loader')
 * @param {string} key - Translation key
 * @param {Object} fallback - Fallback object for nested translations
 * @returns {string} Translated text
 */
export function t(key, fallback = null) {
  const lang = getCurrentLanguage();
  const keys = key.split('.');
  
  let value = translations[lang];
  
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      // Try fallback language
      value = translations[DEFAULT_LANG];
      for (const fk of keys) {
        if (value && typeof value === 'object' && fk in value) {
          value = value[fk];
        } else {
          value = fallback?.[key] || key;
          break;
        }
      }
      break;
    }
  }
  
  return typeof value === 'string' ? value : (fallback?.[key] || key);
}

/**
 * Translate from nested i18n object
 * @param {Object} i18nObj - Object with language keys
 * @param {string} prop - Specific property to extract (e.g., 'name', 'description')
 * @param {string} fallback - Fallback text
 * @returns {string} Translated text for current language
 */
export function translateI18n(i18nObj, prop = 'name', fallback = '') {
  if (!i18nObj) return fallback;
  
  // If it's a string directly, return it
  if (typeof i18nObj === 'string') return i18nObj;
  
  const lang = getCurrentLanguage();
  
  // Get the language object
  let langObj = i18nObj[lang] || i18nObj[DEFAULT_LANG];
  
  if (!langObj) {
    // Try any available language
    const firstKey = Object.keys(i18nObj)[0];
    langObj = firstKey ? i18nObj[firstKey] : null;
  }
  
  if (!langObj) return fallback;
  
  // If we need a specific property and it's an object
  if (prop && typeof langObj === 'object') {
    return langObj[prop] || fallback;
  }
  
  // Return as-is if it's already a string
  return typeof langObj === 'string' ? langObj : fallback;
}

/**
 * Initialize i18n with data from API
 * Priority: URL param > Cookie > Backend default > Hardcoded default
 * @param {Object} branchConfig - Branch configuration from API
 * @param {Object} stateProxy - The Proxy state object from APP_DATA
 */
export function initI18n(branchConfig, stateProxy) {
  proxyState = stateProxy;
  
  // Set available languages from backend
  if (branchConfig?.availableLanguages) {
    availableLanguages = branchConfig.availableLanguages;
  }
  
  // Priority 1: Check URL param (highest priority for sharing links)
  const urlLang = new URLSearchParams(window.location.search).get('lang');
  if (urlLang && availableLanguages.includes(urlLang)) {
    proxyState.lang = urlLang;
    return;
  }
  
  // Priority 2: Check cookie
  const cookieLang = getCookie('lang');
  if (cookieLang && availableLanguages.includes(cookieLang)) {
    proxyState.lang = cookieLang;
    return;
  }
  
  // Priority 3: Backend default
  if (branchConfig?.defaultLanguage && availableLanguages.includes(branchConfig.defaultLanguage)) {
    proxyState.lang = branchConfig.defaultLanguage;
    return;
  }
  
  // Priority 4: Hardcoded default
  proxyState.lang = DEFAULT_LANG;
}

/**
 * Format text with variables
 * @param {string} key - Translation key
 * @param {Object} vars - Variables to replace
 */
export function tFormat(key, vars = {}) {
  let text = t(key);
  
  Object.entries(vars).forEach(([k, v]) => {
    text = text.replace(`{${k}}`, v);
  });
  
  return text;
}
